/**
 * Collection & Search History Tracker
 * Manages browser history to allow users to return to the collection or search page
 * when navigating between product color variants
 */

(function () {
  ("use strict");

  // Check if the feature is enabled via theme settings
  const isFeatureEnabled =
    document.documentElement.dataset.collectionHistoryEnabled === "true";

  // Exit early if feature is disabled
  if (!isFeatureEnabled) {
    return;
  }

  const STORAGE_KEY = "jpress_collection_referrer";
  const SEARCH_STORAGE_KEY = "jpress_search_referrer";
  const COLLECTION_PATH_REGEX = /\/collections\/[^\/]+/;
  const SEARCH_PATH_REGEX = /\/search/;
  const IS_FIRST_PRODUCT_KEY = "jpress_is_first_product";

  /**
   * Store the current collection URL when a user is on a collection page
   */
  function storeCollectionReferrer() {
    const currentPath = window.location.pathname;

    // Check if we're on a collection page
    if (COLLECTION_PATH_REGEX.test(currentPath)) {
      sessionStorage.setItem(STORAGE_KEY, window.location.href);
    }
  }

  /**
   * Store the current search URL when a user is on a search page
   */
  function storeSearchReferrer() {
    const currentPath = window.location.pathname;

    // Check if we're on a search page
    if (SEARCH_PATH_REGEX.test(currentPath)) {
      sessionStorage.setItem(SEARCH_STORAGE_KEY, window.location.href);
    }
  }

  /**
   * Get the stored collection URL
   */
  function getCollectionReferrer() {
    return sessionStorage.getItem(STORAGE_KEY);
  }

  /**
   * Get the stored search URL
   */
  function getSearchReferrer() {
    return sessionStorage.getItem(SEARCH_STORAGE_KEY);
  }

  /**
   * Get any stored referrer (collection or search)
   */
  function getReferrer() {
    return getCollectionReferrer() || getSearchReferrer();
  }

  /**
   * Get the type of referrer ('collection' or 'search')
   */
  function getReferrerType() {
    if (getCollectionReferrer()) return 'collection';
    if (getSearchReferrer()) return 'search';
    return null;
  }

  /**
   * Check if this is the first product page visit from collection
   */
  function isFirstProductVisit() {
    return sessionStorage.getItem(IS_FIRST_PRODUCT_KEY) === "true";
  }

  /**
   * Mark that we've visited a product page
   */
  function markProductVisited() {
    sessionStorage.setItem(IS_FIRST_PRODUCT_KEY, "false");
  }

  /**
   * Add collection_ref or search_ref parameter to a link href, preserving existing parameters
   */
  function addReferrerToHref(href) {
    if (!href) return href;

    const referrerType = getReferrerType();
    if (!referrerType) return href;

    const paramName = referrerType === 'collection' ? 'collection_ref' : 'search_ref';

    // Check if the parameter already exists
    if (href.includes(`${paramName}=`)) {
      return href;
    }

    // Use URL API for proper parameter handling
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set(paramName, "preserved");
      return url.pathname + url.search;
    } catch (e) {
      // Fallback to simple string concatenation if URL parsing fails
      const separator = href.includes("?") ? "&" : "?";
      return href + separator + paramName + "=preserved";
    }
  }

  /**
   * Process variant links - add collection_ref/search_ref and click handlers
   */
  function processVariantLinks() {
    const referrer = getReferrer();
    if (!referrer) return;

    // Find all product sibling links (color variant thumbnails)
    const siblingLinks = document.querySelectorAll(
      ".product-siblings .block-swatch__link",
    );

    siblingLinks.forEach((link) => {
      const currentHref = link.getAttribute("href");

      // Add referrer as URL parameter
      if (currentHref) {
        const newHref = addReferrerToHref(currentHref);
        if (newHref !== currentHref) {
          link.setAttribute("href", newHref);
        }
      }

      // Only add click handler if not already added (check for a data attribute)
      if (!link.hasAttribute("data-history-handler")) {
        link.setAttribute("data-history-handler", "true");

        link.addEventListener("click", function (e) {
          e.preventDefault();
          const targetUrl = this.getAttribute("href");

          // Always use window.location.replace() for ALL color variant navigation
          // This ensures no product variants are added to browser history
          // So clicking back will always skip directly to the collection/search
          window.location.replace(targetUrl);
        });
      }
    });
  }

  /**
   * Check if collection_ref or search_ref parameter exists in current URL
   */
  function hasReferrerParam() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has("collection_ref") || urlParams.has("search_ref");
  }

  /**
   * Ensure collection_ref or search_ref stays in the URL even when Shopify updates it
   */
  function ensureReferrerInUrl() {
    const referrerType = getReferrerType();
    if (!referrerType) return;

    const paramName = referrerType === 'collection' ? 'collection_ref' : 'search_ref';
    const url = new URL(window.location.href);

    if (!url.searchParams.has(paramName)) {
      url.searchParams.set(paramName, "preserved");
      history.replaceState(history.state, "", url.pathname + url.search);
    }
  }

  /**
   * Initialize on product pages - add referrer to product links
   */
  function initProductPage() {
    const isProductPage = document.body.classList.contains("template-product");

    if (!isProductPage) return;

    // Process links on initial load
    processVariantLinks();

    // Watch for DOM changes (Shopify's variant selector updates links)
    const productSiblingsContainer =
      document.querySelector(".product-siblings");

    if (productSiblingsContainer) {
      const observer = new MutationObserver((mutations) => {
        // Check if any mutations affected the links
        const linksChanged = mutations.some((mutation) => {
          return (
            mutation.type === "attributes" &&
            mutation.target.classList.contains("block-swatch__link")
          );
        });

        if (linksChanged) {
          // Re-process links when Shopify updates them
          processVariantLinks();
        }
      });

      // Observe changes to attributes on child elements
      observer.observe(productSiblingsContainer, {
        attributes: true,
        attributeFilter: ["href"],
        subtree: true,
      });
    }

    // Preserve referrer parameter when variant selector updates the URL
    const referrer = getReferrer();
    if (referrer) {
      // Override history methods to maintain referrer parameter
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function () {
        const result = originalPushState.apply(this, arguments);
        ensureReferrerInUrl();
        return result;
      };

      history.replaceState = function () {
        const result = originalReplaceState.apply(this, arguments);
        ensureReferrerInUrl();
        return result;
      };

      // Also watch for popstate events
      window.addEventListener("popstate", ensureReferrerInUrl);

      // Add referrer to URL on initial page load if not present
      ensureReferrerInUrl();
    }
  }

  /**
   * Initialize on collection pages - store the referrer
   */
  function initCollectionPage() {
    const isCollectionPage = document.body.classList.contains(
      "template-collection",
    );

    if (!isCollectionPage) return;

    // Clear search referrer since we're now on a collection page
    sessionStorage.removeItem(SEARCH_STORAGE_KEY);

    storeCollectionReferrer();

    // Add collection referrer to all product links on the collection page
    const productLinks = document.querySelectorAll(
      '.product-item a[href*="/products/"]',
    );

    productLinks.forEach((link) => {
      link.addEventListener("click", function () {
        // Store collection URL right before navigation
        storeCollectionReferrer();
      });
    });
  }

  /**
   * Initialize on search pages - store the referrer
   */
  function initSearchPage() {
    const isSearchPage = document.body.classList.contains("template-search");

    if (!isSearchPage) return;

    // Clear collection referrer since we're now on a search page
    sessionStorage.removeItem(STORAGE_KEY);

    storeSearchReferrer();

    // Add search referrer to all product links on the search page
    const productLinks = document.querySelectorAll(
      '.product-item a[href*="/products/"]',
    );

    productLinks.forEach((link) => {
      link.addEventListener("click", function () {
        // Store search URL right before navigation
        storeSearchReferrer();
      });
    });
  }

  /**
   * Clear referrer when navigating to other pages
   */
  function clearReferrerOnOtherPages() {
    const isProductPage = document.body.classList.contains("template-product");
    const isCollectionPage = document.body.classList.contains(
      "template-collection",
    );
    const isSearchPage = document.body.classList.contains("template-search");

    // Clear if we're on neither product, collection, nor search page
    if (!isProductPage && !isCollectionPage && !isSearchPage) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SEARCH_STORAGE_KEY);
      sessionStorage.removeItem(IS_FIRST_PRODUCT_KEY);
    }
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    initCollectionPage();
    initSearchPage();
    initProductPage();
    clearReferrerOnOtherPages();
  }
})();
